import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import useAuth from "../../hooks/useAuth";
import apiClient from "../../store/services/apiClient";
import styles from "./AutoPromoTab.module.css";
import { useDispatch } from "react-redux";
import { getUsersByID } from "../../store/slices/user";

export default function AutoPromoTab() {
  const { user } = useAuth();
  const dispatch = useDispatch();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pending, setPending] = useState(null);

  const [userDetail, setUserDetail] = useState(null);
  const [available, setAvailable] = useState([]);

  const [selectedId, setSelectedId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState("");
  const [highlightIdx, setHighlightIdx] = useState(null);
  const [loading, setLoading] = useState(false);

  const disabledSelect =
    userDetail?.PromotionStatus === "ACTIVE" ||
    !!userDetail?.withdrawConditionType;

  const makeImageUrl = (path) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    const base = process.env.REACT_APP_API_BASE_URL || "";
    const sep = path.startsWith("/") ? "" : "/";
    return `${base}${sep}${path}`;
  };

  const promoCards = useMemo(() => {
    return (available || []).map((p) => ({
      id: p?.PromotionID,
      detail: p?.Detail,
      image: makeImageUrl(p?.imageUrl),
      raw: p,
    }));
  }, [available]);

  useEffect(() => {
    if (!user) return;
    setSelectedId(user?.PromotionID || null);
    setSelectedDetail(user?.PromotionDetail || "");
  }, [user]);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    const fetchAll = async () => {
      try {
        const [{ data: userRes }, { data: promoRes }] = await Promise.all([
          apiClient.get(`/usersystem/${user.id}`, {
            headers: {
              "Cache-Control": "no-cache, no-store, must-revalidate",
              Pragma: "no-cache",
              Expires: "0",
            },
          }),
          apiClient.get(`/usersystem/getavailablepromotion/${user.id}`),
        ]);
        const u = Array.isArray(userRes?.data)
          ? userRes.data[0]
          : userRes?.data;
        setUserDetail(u || null);
        setAvailable(promoRes?.data || promoRes || []);
      } catch (e) {
        console.error("load data error", e?.message || e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [user]);

  useEffect(() => {
    if (!promoCards?.length) return;
    const idToFind = userDetail?.PromotionID || selectedId;
    const idx = promoCards.findIndex((p) => p.id === idToFind);
    setHighlightIdx(idx >= 0 ? idx : null);
  }, [promoCards, userDetail, selectedId]);

  const handleSelectCard = (index) => {
    if (disabledSelect) return;
    const item = promoCards[index];
    if (!item) return;
    setPending({ index, item });
    setConfirmOpen(true);
  };

  const toast = (msg) => {
    try {
      if (window?.dispatchEvent) {
        window.dispatchEvent(
          new CustomEvent("app:toast", { detail: { message: msg } }),
        );
      }
    } catch (_) {}
  };

  const applyPromotion = async (promotionId, detail) => {
    try {
      const body = new URLSearchParams();
      body.set("PromotionID", promotionId);
      body.set("PromotionDetail", detail);
      await apiClient.put(
        `/usersystem/promotion/${user?.id}`,
        body.toString(),
        {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        },
      );
      const [{ data: userRes }, { data: promoRes }] = await Promise.all([
        apiClient.get(`/usersystem/${user.id}`),
        apiClient.get(`/usersystem/getavailablepromotion/${user.id}`),
      ]);
      const u = Array.isArray(userRes?.data) ? userRes.data[0] : userRes?.data;
      setUserDetail(u || null);
      setAvailable(promoRes?.data || promoRes || []);
      if (user?.id) dispatch(getUsersByID(user.id));
      if (window?.dispatchEvent) {
        window.dispatchEvent(
          new CustomEvent("app:userUpdate", { detail: { userId: user?.id } }),
        );
      }
      toast(
        promotionId && promotionId !== "NULL"
          ? "เลือกโปรโมชั่นเรียบร้อย"
          : "เปลี่ยนเป็นไม่รับโปรโมชั่นแล้ว",
      );
    } catch (e) {
      console.error("applyPromotion error", e?.message || e);
      toast("ไม่สามารถอัปเดตโปรโมชั่นได้");
    }
  };

  const handleConfirm = async () => {
    if (!pending) return;
    const { item, index } = pending;
    setSelectedId(item.id);
    setSelectedDetail(item.detail);
    setHighlightIdx(index);
    setConfirmOpen(false);
    setPending(null);
    await applyPromotion(item.id, item.detail);
  };

  const handleNonPro = async () => {
    setSelectedId(null);
    setSelectedDetail("");
    setHighlightIdx(null);
    await applyPromotion("NULL", "NULL");
  };

  return (
    <div className={styles.wrapper}>
      {/* ── Current Status ── */}
      <div className={styles.statusStrip}>
        <div className={styles.statusStripIcon}>
          <Icon
            icon={
              selectedDetail
                ? "solar:verified-check-bold"
                : "solar:close-circle-bold"
            }
            width={22}
            height={22}
          />
        </div>
        <div className={styles.statusStripBody}>
          <div className={styles.statusStripLabel}>โปรโมชั่นที่เลือกไว้</div>
          <div
            className={`${styles.statusStripValue} ${selectedDetail ? styles.statusActive : styles.statusNone}`}
          >
            {selectedDetail || "ไม่ได้รับโปรโมชั่น"}
          </div>
          {selectedDetail && (
            <div className={styles.statusBadge}>
              <Icon icon="solar:check-circle-bold" width={12} height={12} />
              กำลังใช้งาน
            </div>
          )}
        </div>
      </div>

      {/* ── Warning ── */}
      {disabledSelect && (
        <div className={styles.warningCard}>
          <div className={styles.warningCardIcon}>
            <Icon icon="solar:danger-triangle-bold" width={20} height={20} />
          </div>
          <div className={styles.warningCardBody}>
            <p className={styles.warningCardTitle}>
              ไม่สามารถเปลี่ยนโปรโมชั่นได้
            </p>
            <p className={styles.warningCardDesc}>
              ท่านอยู่ระหว่างใช้โปรโมชั่น หรือมียอดที่ต้องเล่น
              กรุณาเคลียร์ยอดเล่นให้ครบก่อน
            </p>
          </div>
        </div>
      )}

      {/* ── Section Header ── */}
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>
          <Icon icon="solar:gift-bold-duotone" width={18} height={18} />
          โปรโมชั่นที่เลือกได้
        </h3>
        {!disabledSelect && (
          <button onClick={handleNonPro} className={styles.noPromoBtn}>
            <Icon icon="solar:close-circle-linear" width={18} height={18} />
            ไม่รับโปรโมชั่น
          </button>
        )}
      </div>

      {/* ── Promo Grid ── */}
      {loading ? (
        <div className={styles.loadingBox}>
          <div className={styles.spinner} />
          <p>กำลังโหลดข้อมูลโปรโมชั่น...</p>
        </div>
      ) : promoCards && promoCards.length > 0 ? (
        <div className={styles.promoGrid}>
          {promoCards.map((it, idx) => (
            <div
              key={`${it.id}-${idx}`}
              className={[
                styles.promoCard,
                highlightIdx === idx && styles.promoCardSelected,
                disabledSelect && styles.promoCardDisabled,
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => handleSelectCard(idx)}
            >
              <div className={styles.cardThumb}>
                {it.image ? (
                  <img
                    src={it.image}
                    alt={it.detail}
                    className={styles.cardThumbImg}
                  />
                ) : (
                  <div className={styles.cardPlaceholder}>
                    <Icon
                      icon="solar:gallery-bold-duotone"
                      width={36}
                      height={36}
                    />
                  </div>
                )}
                {highlightIdx === idx && (
                  <div className={styles.cardCheck}>
                    <Icon icon="solar:check-read-bold" width={16} height={16} />
                  </div>
                )}
              </div>
              <div className={styles.cardBody}>
                <p className={styles.cardName}>{it.detail}</p>
                <p
                  className={`${styles.cardHint} ${highlightIdx === idx ? styles.cardHintActive : ""}`}
                >
                  {highlightIdx === idx
                    ? "กำลังใช้งาน"
                    : disabledSelect
                      ? "ไม่สามารถเลือกได้"
                      : "แตะเพื่อเลือก"}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <Icon icon="solar:gift-bold-duotone" width={28} height={28} />
          </div>
          <h3 className={styles.emptyTitle}>ไม่มีโปรโมชั่น</h3>
          <p className={styles.emptyDesc}>ไม่มีโปรโมชั่นให้เลือกในขณะนี้</p>
        </div>
      )}

      {/* ── Confirm Modal ── */}
      {confirmOpen && pending && (
        <div className={styles.overlay} onClick={() => setConfirmOpen(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalTop}>
              <div className={styles.modalTopIcon}>
                <Icon icon="solar:verified-check-bold" width={24} height={24} />
              </div>
              <h3 className={styles.modalTopTitle}>ยืนยันการเลือกโปรโมชั่น</h3>
            </div>

            {pending.item?.image && (
              <div className={styles.modalImgWrap}>
                <img
                  src={pending.item.image}
                  alt={pending.item.detail}
                  className={styles.modalImg}
                />
              </div>
            )}

            <div className={styles.modalBody}>
              <p className={styles.modalQuestion}>
                ท่านต้องการเลือกโปรโมชั่นนี้หรือไม่?
              </p>
              <div className={styles.modalPromoName}>
                {pending.item?.detail}
              </div>
            </div>

            <div className={styles.modalActions}>
              <button
                onClick={() => setConfirmOpen(false)}
                className={styles.btnCancel}
              >
                ยกเลิก
              </button>
              <button onClick={handleConfirm} className={styles.btnConfirm}>
                <Icon icon="solar:check-read-bold" width={16} height={16} />
                ยืนยันเลือก
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
